import { ref } from 'vue'
import {
    GoogleAuthProvider,
    FacebookAuthProvider,
    signInWithPopup,
} from 'firebase/auth'
import { projectAuth, projectFirestore } from '@/firebase/config'
import { collection, query, where, getDocs, serverTimestamp, documentId } from 'firebase/firestore'
import useCollection from './useDocument'

export function useSignup() {
    const loading = ref(false)
    const error = ref(null)
    const user = ref(null)

    // Cache free plan ID to avoid repeated queries
    let freePlanIdCache = null
    const getFreePlanId = async () => {
        if (freePlanIdCache) return freePlanIdCache

        const q = query(collection(projectFirestore, 'plans'), where('planType', '==', 'free'))
        const snapshot = await getDocs(q)
        if (!snapshot.empty) {
            freePlanIdCache = snapshot.docs[0].id
            console.log('Free plan ID:', freePlanIdCache)
            return freePlanIdCache
        }
        throw new Error('Free plan not found')
    }

    // Generic signup handler for Google / Facebook
    const handleSignup = async (providerType) => {
        loading.value = true
        error.value = null
        try {
            let provider
            if (providerType === 'google') provider = new GoogleAuthProvider()
            if (providerType === 'facebook') {
                provider = new FacebookAuthProvider()
                provider.addScope('email') // request email explicitly
            }

            const result = await signInWithPopup(projectAuth, provider)
            const fbUser = result.user
            user.value = fbUser

            if (!fbUser) return null

            // Run user existence check (uid + provider) AND fetch free plan in parallel
            const [existingUserSnapshot, planId] = await Promise.all([
                getDocs(
                    query(
                        collection(projectFirestore, 'users'),
                        where(documentId(), '==', fbUser.uid),
                        where('provider', '==', providerType)
                    )
                ),
                getFreePlanId()
            ])

            if (!existingUserSnapshot.empty) {
                console.log('User already exists')
                return fbUser
            }

            // New user -> insert into Firestore
            const data = {
                displayName: fbUser.displayName,
                email: fbUser.email,
                userName: fbUser.displayName,
                userNameLowerCase: fbUser.displayName?.toLowerCase(),
                uid: fbUser.uid,
                photoURL: fbUser.photoURL,
                createdAt: serverTimestamp(),
                role: 'User',
                provider: providerType,
                planId, // assign free plan for both Google & Facebook
                status: true,
                subscriptionStart: serverTimestamp(),
                subscriptionEnd: serverTimestamp(),

            }

            const { setDocs } = useCollection('users')
            await setDocs(fbUser.uid, data)

            console.log(`${providerType} signup success`, fbUser)
            return fbUser
        } catch (err) {
            error.value = err.message
            throw err
        } finally {
            loading.value = false
        }
    }

    const signupWithGoogle = () => handleSignup('google')
    const signupWithFacebook = () => handleSignup('facebook')

    return {
        loading,
        error,
        user,
        signupWithGoogle,
        signupWithFacebook,
    }
}